import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { closeSnackbar, enqueueSnackbar, type SnackbarKey } from "_shared/_providers/snackbars/snackbars";
import { TOUCH_TARGET, touchTargetQuery } from "_shared/_views/touch-target";

/**
 * A success toast that names a change and offers to undo it until it is dismissed.
 *
 * MUI's Snackbar pauses its own timer on focus, but restarts it when the pointer leaves, even with
 * focus still on Undo, so a keyboard user could have it taken away under them. The toast is raised as
 * persisting and closed here instead, on a timer that waits while the reader points at it or has
 * focus in it. Undo, or closing the toast while focus is in it, sends focus back to where the change
 * was made.
 *
 * Module-level, so none of it is compiled into the hooks that call it.
 */

export type UndoSnackbarOptions = Readonly<{
  message: string;
  durationMs: number;
  /** Where focus goes after Undo, or when the toast closes with focus inside it. */
  returnFocus: () => void;
  undo: () => void;
  /** `status` for a confirmation a screen reader should not be interrupted by. Defaults to `alert`. */
  role?: "alert" | "status";
}>;

/** A small button, about 30px tall, that grows to a fingertip on a phone or under a coarse pointer. */
const UndoButton = styled(Button)(({ theme }) => ({
  [touchTargetQuery(theme)]: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET },
}));

export function pausableDismissal(close: () => void, durationMs: number) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let hovered = false;
  let focused = false;
  const start = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(close, durationMs);
  };
  const update = () => {
    if (hovered || focused) {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    } else if (timer === undefined) {
      start();
    }
  };
  start();
  return {
    setHovered(value: boolean) {
      hovered = value;
      update();
    },
    setFocused(value: boolean) {
      focused = value;
      update();
    },
    cancel() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}

export function offerUndo(options: UndoSnackbarOptions): SnackbarKey {
  const { message, durationMs, returnFocus, undo, role } = options;
  let key: SnackbarKey | undefined;
  const dismissal = pausableDismissal(() => {
    if (key !== undefined) closeSnackbar(key);
  }, durationMs);
  const snackbarId = `undo-${Guid.generate().value}`;

  key = enqueueSnackbar(message, {
    variant: "success",
    persist: true,
    role,
    snackbarProps: {
      id: snackbarId,
      onMouseEnter: () => dismissal.setHovered(true),
      onMouseLeave: () => dismissal.setHovered(false),
      onFocus: () => dismissal.setFocused(true),
      onBlur: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) dismissal.setFocused(false);
      },
    },
    onClose: () => {
      dismissal.cancel();
      // Closing takes the focused Undo away with it; the reader goes back to where they started.
      if (document.getElementById(snackbarId)?.contains(document.activeElement)) returnFocus();
    },
    action: (snackbarKey) => (
      <UndoButton
        color="inherit"
        size="small"
        onClick={() => {
          closeSnackbar(snackbarKey);
          returnFocus();
          undo();
        }}
      >
        Undo
      </UndoButton>
    ),
  });
  return key;
}
